/**
 * MediaSession + Picture-in-Picture wiring per research R12.
 *
 * Three lines of code buy us: keyboard media keys on laptops, lock-screen
 * controls on mobile, Bluetooth headset buttons, OS "Now Playing" metadata.
 * Users feel this without knowing why the app feels premium.
 */

import type { Item } from "../../types.ts";

export interface MediaSessionHandlers {
  play?: () => void;
  pause?: () => void;
  next?: () => void;
  prev?: () => void;
  seekBy?: (seconds: number) => void;
}

export function setMediaSessionMetadata(item: Item | null): void {
  if (typeof navigator === "undefined" || !("mediaSession" in navigator)) return;
  if (!item) {
    navigator.mediaSession.metadata = null;
    return;
  }
  try {
    navigator.mediaSession.metadata = new MediaMetadata({
      title: item.title,
      artist: item.uploader ?? "archive.org",
      album: item.year ?? "",
      artwork: item.thumbUrl
        ? [{ src: item.thumbUrl, sizes: "180x180", type: "image/jpeg" }]
        : [],
    });
  } catch {
    /* Older browsers without MediaMetadata constructor — swallow. */
  }
}

export function setMediaSessionHandlers(handlers: MediaSessionHandlers): void {
  if (typeof navigator === "undefined" || !("mediaSession" in navigator)) return;
  const ms = navigator.mediaSession;
  try {
    ms.setActionHandler("play", handlers.play ?? null);
    ms.setActionHandler("pause", handlers.pause ?? null);
    ms.setActionHandler("nexttrack", handlers.next ?? null);
    ms.setActionHandler("previoustrack", handlers.prev ?? null);
    ms.setActionHandler(
      "seekbackward",
      handlers.seekBy ? () => handlers.seekBy?.(-10) : null,
    );
    ms.setActionHandler("seekforward", handlers.seekBy ? () => handlers.seekBy?.(10) : null);
  } catch {
    /* swallow */
  }
}
